import { Box, Grid, Typography } from '@mui/material';
import useAuth from 'hooks/useAuth';
import processTextBody from 'utils/processTextBody';

const AbstractContentSection = ({ data }) => {
    const title = data?.title || 'Untitled';
    const typeName = data?.type?.name || '';
    const submissionNumber = data?.number || '';
    const { user } = useAuth()

    const authorsWithInstitution = (() => {
        if (!data?.authors) return [];
        const authors = [...data.authors];
        authors.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        const institutionMap = new Map();
        let institutionCounter = 1;
        return authors.map((author) => {
            if (!institutionMap.has(author.institution)) {
                institutionMap.set(author.institution, institutionCounter++);
            }
            return {
                ...author,
                institutionNumber: institutionMap.get(author.institution),
            };
        });
    })();

    const institutionList = (() => {
        if (!authorsWithInstitution.length) return [];
        const map = new Map();
        authorsWithInstitution.forEach((author) => {
            if (!map.has(author.institution)) {
                map.set(author.institution, author.institutionNumber);
            }
        });
        return Array.from(map).map(([institution, number]) => ({
            institution,
            number,
        }));
    })();

    const sections = (() => {
        const structures = data?.type?.structures || [];
        const body = data?.body || '';
        return processTextBody(structures, body);
    })();

    const keywordsText = (() => {
        const arr = data?.data?.keyword || [];
        if (!arr || arr.length === 0) return '';
        return arr.join(', ');
    })();

    const { figureUrl, figureCaption } = (() => {
        const fileUrl = data?.data?.figure?.fileUrl;
        const url = fileUrl ? `${process.env.REACT_APP_API_BASE_URL}/api/${fileUrl}` : '';
        const caption = data?.data?.figure?.caption || '';
        return { figureUrl: url, figureCaption: caption };
    })();

    return (
        <>
            <Box mb={4}>
                {submissionNumber ? (
                    <Box mt={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                            No: {submissionNumber}
                        </Typography>
                    </Box>
                ) : null}
                {typeName ? (
                    <Typography variant="subtitle2" color="text.secondary" 
                        sx={{ mt: 0.5, mb: 2, fontStyle: 'italic' }}>
                        {typeName}
                    </Typography>
                ) : null}

                <Typography
                    align="center"
                    variant="h6"
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 700,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            { user && user.role != 'judge' && (
                <>
                    <Box mb={1} display="flex" justifyContent="center" px={{ xs: 1, md: 6 }}>
                        <Typography align="center" variant="body1" color="text.primary" sx={{ lineHeight: 1.8 }}>
                            {authorsWithInstitution.map((author, index) => (
                                <span key={author.id || index} style={{ marginRight: 4 }}>
                                    <span
                                        style={{
                                            textDecoration: index === 0 ? 'underline' : 'none',
                                        }}
                                    >
                                        {author.name}
                                    </span>
                                    <sup>{author.institutionNumber}</sup>
                                    {index < authorsWithInstitution.length - 1 ? ',' : ''}
                                </span>
                            ))}
                        </Typography>
                    </Box>

                    <Box mb={4}>
                        {institutionList.map(({ institution, number }, idx) => (
                            <Box key={idx} display="flex" justifyContent="center">
                                <Typography align="center" variant="body2" color="text.secondary">
                                    <sup>{number}</sup> {institution}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            <Box px={{ xs: 1, md: 4 }}>
                {sections.map((sec, idx) => (
                    <Box key={idx} mb={2.5}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {sec.keyword}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'justify', color: 'text.primary' }}>
                            {sec.text}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box mt={4} px={{ xs: 1, md: 4 }}>
                <Typography variant="body2">
                    <strong>Keywords: </strong>
                    {keywordsText}
                </Typography>
            </Box>

            {figureUrl ? (
                <Box mt={5}>
                    <Box display="flex" justifyContent="center" mb={2}>
                        <Typography align="center" variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {figureCaption}
                        </Typography>
                    </Box>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} md={8}>
                            <Box display="flex" justifyContent="center" sx={{ px: { xs: 1, md: 0 } }}>
                                <img
                                    src={figureUrl}
                                    alt="figure"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        maxHeight: 420,
                                        objectFit: 'contain',
                                        borderRadius: 8,
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            ) : null}
        </>
    );
};

export default AbstractContentSection;
